import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentOutboundElement,
  updateOutboundElement,
  updateOutboundElementField,
  selectArtifactComplex,
  selectSelectedSchema,
  selectCurrentArtifactDictionaryId,
  selectOutboundSchemaTable,
  selectAllOutboundSchemaTables,
  setOutboundSchemaTable,
  selectLoopRowsList,
  clearItemsFromEditBatch,
  removeItemFromEditBatch,
  selectStagedOutboundEdits,
} from "@/features/mapSchema/mapSchemaSlice";
import {
  Typography,
  Container,
  Grid,
  Stack,
  IconButton,
  InputLabel,
  Input,
  Tab,
  Tabs,
  Box,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import axios from "@/features/axiosHelper";
import { ArrowLeftIcon, ArrowRightIcon } from "@/ImageRepo";
import Dropdown from "@/components/Dropdown/Dropdown";
import { createAlert } from "@/features/homepage/alertSliceList";
import PreviewArtifact from "./PreviewArtifact";
import ScriptEditor from "@/components/GenericComponents/ScriptEditor";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";

/* SHEP-776 Presets Tab is Deprecated
const PresetsTab = () => {
  const dispatch = useDispatch();
  const element = useSelector(selectCurrentOutboundElement);

  return (
    <Box style={{ marginTop: 12 }}>
      {!element.DataValue.includes("\r\n") && (
        <Typography variant="h6" style={{ marginLeft: 24 }}>
          No preset values for this element
        </Typography>
      )}
      {element.DataValue.includes("\r\n") && element.SourceId === "A" && (
        <>
          <Box style={{ marginBottom: 12 }}>
            This element has a single set value. Modify in Data Value.
          </Box>
          <Stack
            flexDirection="row"
            justifyContent={"flex-start"}
            alignItems={"center"}
          >
            {element.DataValue.trim()
              .split("\r\n")
              .map((record, i) => {
                if (i === 0) {
                  return (
                    <Typography
                      key={i}
                      variant="h6"
                      style={{ marginRight: 30 }}
                    >
                      {record}
                    </Typography>
                  );
                } else if (i === 1) {
                  return record + ".";
                } else if (i === 2) {
                  return record;
                }
              })}
          </Stack>
        </>
      )}
      {element.DataValue.includes("\r\n") && element.SourceId === "U" && (
        <Stack flexDirection="column" justifyContent={"space-between"}>
          {element.DataValue.trim()
            .split("\r\n")
            .map((record, i) => {
              if (i === 0) {
                return (
                  <Typography key={i} variant="h6">
                    {record}
                  </Typography>
                );
              }
              if (i === 1) {
                //second value is count of options
                return;
              }
              let vals = record.split(",");
              let val = vals[0] ?? "";
              let label = vals[1] ?? "";
              return (
                <Stack
                  key={i}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ margin: 6 }}
                >
                  <Stack flexDirection="row">
                    <InputLabel style={{ width: 100 }}>{val}</InputLabel>
                    <InputLabel>{label}</InputLabel>
                  </Stack>
                  <Button
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation(); // don't select this row after clickinge);
                      dispatch(
                        updateOutboundElementField({
                          key: "UserMap",
                          value: val,
                        })
                      );
                    }}
                    style={{ width: 70, minWidth: 70 }}
                  >
                    Apply
                  </Button>
                </Stack>
              );
            })}
        </Stack>
      )}
    </Box>
  );
};
*/

const ArtifactDataPanel = ({ cmEditor }) => {
  const complex = useSelector(selectArtifactComplex);

  const [selectedArtifact, setSelectedArtifact] = useState(0);
  const [searchText, setSearchText] = useState("");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Stack flexDirection={"row"} justifyContent={"space-between"} sx={{ flexShrink: 0 }}>
        <Dropdown
          width={300}
          onChange={(e) => {
            setSelectedArtifact(e.target.value - 1); //dropdown doesn't like 0 as key
          }}
          value={selectedArtifact + 1}
          options={complex.map((x, i) => {
            return { key: i + 1, label: x.ObjectName };
          })}
        />
        <TextField
          size="small"
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          label="Search"
        ></TextField>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {complex[selectedArtifact]?.Fields.map((x, i) => {
          if (
            searchText.length > 2 &&
            !x.Name.toUpperCase().includes(searchText.toUpperCase())
          )
            return;
          return (
            <Stack
              key={i}
              flexDirection="row"
              alignItems="center"
              sx={{
                m: 0,
                p: "4px 6px",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
              }}
            >
              <ShepTooltip title={x.Name} placement="top-start">
                <InputLabel
                  sx={{
                    width: "40%",
                    minWidth: 0,
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {x.Name}
                </InputLabel>
              </ShepTooltip>
              <ShepTooltip title={x.Value ?? ""} placement="top-start">
                <InputLabel
                  sx={{
                    width: "40%",
                    minWidth: 0,
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    px: 1,
                  }}
                >
                  {x.Value}
                </InputLabel>
              </ShepTooltip>
              <Box sx={{ width: "20%", minWidth: 0, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
                <ShepHorizontalMenu
                  actions={[
                    {
                      label: "Apply",
                      onClick: (e) => {
                        e.stopPropagation();
                        const insertTransaction = cmEditor.state.replaceSelection(
                          complex[selectedArtifact].ObjectName + "." + x.Name
                        );
                        cmEditor.dispatch(insertTransaction);
                      },
                      style: { width: 60, minWidth: 60, height: 20 },
                    },
                  ]}
                />
              </Box>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
};

const AdvancedPanel = () => {
  const dispatch = useDispatch();

  const element = useSelector(selectCurrentOutboundElement);

  const elementSchema = [
    {
      field: "SourceId",
      label: "Source Id",
      type: "dropdown",
      values: [
        { key: "A", label: "A - Accounting System" },
        { key: "D", label: "D - Default or Fixed" },
        { key: "S", label: "S - System or Special" },
        { key: "T", label: "T - Turnaround" },
        { key: "U", label: "U - User Input" },
      ],
    },
    {
      field: "DataType",
      label: "Data Type",
      type: "dropdown",
      values: [
        { key: "AN", label: "AN - Alphanumeric" },
        { key: "DT", label: "DT - Date" },
        { key: "ID", label: "ID - Identifier" },
        { key: "R", label: "R - Real number" },
        { key: "R2", label: "R2 - Real number" },
        { key: "N0", label: "N0 - Numeric 0 decimals" },
        { key: "N2", label: "N2 - Numeric 2 decimals" },
      ],
    },
    {
      field: "MinLength",
      label: "Min Length",
      type: "number",
    },
    {
      field: "MaxLength",
      label: "Max Length",
      type: "number",
    },
    {
      field: "RequireCode",
      label: "Require Code",
      type: "dropdown",
      values: [
        { key: "M", label: "M - Mandatory" },
        { key: "O", label: "O - Optional" },
        { key: "X", label: "X - Conditional" },
      ],
    },
    {
      field: "HandleMethod",
      label: "Handle Method",
      type: "dropdown",
      values: [
        { key: "", label: "" },
        { key: "CHANGETO", label: "CHANGETO" },
        { key: "EMPTYDROPQUAL", label: "EMPTYDROPQUAL" },
        { key: "EMPTYDROPSEG", label: "EMPTYDROPSEG" },
        { key: "EMPTYDROPVALUE", label: "EMPTYDROPVALUE" },
        { key: "EMPTYSHOWZERO", label: "EMPTYSHOWZERO" },
        { key: "EXCHANGE", label: "EXCHANGE" },
        { key: "ONEDROP", label: "ONEDROP" },
        { key: "ONEDROPSEG", label: "ONEDROPSEG" },
        { key: "SEQUENCE", label: "SEQUENCE" },
        { key: "SHOWNEGATIVE", label: "SHOWNEGATIVE" },
        { key: "SHOWPOSITIVE", label: "SHOWPOSITIVE" },
        { key: "SUM", label: "SUM" },
        { key: "ZERODROP", label: "ZERODROP" },
        { key: "ZERODROPQUAL", label: "ZERODROPQUAL" },
        { key: "ZERODROPSEG", label: "ZERODROPSEG" },
        { key: "ZERODROPVALUE", label: "ZERODROPVALUE" },
      ],
    },
    {
      field: "ValidateCode",
      label: "Validate Code",
      type: "dropdown",
      values: [
        { key: "", label: "" },
        { key: "E", label: "E - Error when blank" },
      ],
    },
    {
      field: "Position",
      label: "Loop Position",
      type: "number",
    },
  ];

  const half = Math.ceil(elementSchema.length / 2);
  const leftColumn = elementSchema.slice(0, half);
  const rightColumn = elementSchema.slice(half);

  const renderField = (schema, i) => (
    <Box key={i}>
      {(schema.type === "number" || schema.type === "string") && (
        <Stack
          flexDirection={"row"}
          alignItems={"center"}
          style={{ marginBottom: 8 }}
        >
          <InputLabel style={{ width: 120, height: 20, fontSize: "0.8rem" }}>
            {schema.label}:
          </InputLabel>
          <Input
            style={{ marginLeft: 6, width: 160 }}
            value={element[schema.field]}
            onChange={(e) => {
              let value = e.target.value;
              if (schema.type === "number" && /^\d*$/.test(value))
                dispatch(
                  updateOutboundElementField({
                    key: schema.field,
                    value: value,
                  })
                );
            }}
          />
        </Stack>
      )}
      {schema.type === "dropdown" && (
        <Stack
          flexDirection={"row"}
          alignItems={"center"}
          style={{ marginBottom: 8 }}
        >
          <InputLabel style={{ width: 120, height: 20, fontSize: "0.8rem" }}>
            {schema.label}:
          </InputLabel>
          <Dropdown
            width={160}
            onChange={(e) => {
              dispatch(
                updateOutboundElementField({
                  key: schema.field,
                  value: e.target.value,
                })
              );
            }}
            value={element[schema.field].trim()}
            options={schema.values.map((x) => {
              return { key: x.key, label: x.label };
            })}
          />
        </Stack>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Stack direction="row" sx={{ gap: 2, flexShrink: 0 }}>
        <Box sx={{ flex: 1 }}>
          {leftColumn.map(renderField)}
        </Box>
        <Box sx={{ flex: 1 }}>
          {rightColumn.map(renderField)}
        </Box>
      </Stack>
      <Box sx={{ flexShrink: 0 }}>
        <InputLabel style={{ fontSize: "0.8rem" }}>Data Value:</InputLabel>
        <Input
          variant="filled"
          style={{
            marginLeft: 10,
            height: "unset",
            width: "100%",
          }}
          rows={3}
          value={element.DataValue}
          onChange={(e) => {
            dispatch(
              updateOutboundElementField({
                key: "DataValue",
                value: e.target.value.replaceAll("\n", "\r\n"),
              })
            );
          }}
          multiline
          sx={{
            alignItems: "flex-start",
          }}
        />
      </Box>
    </Box>
  );
};

const tabIds = {
  Presets: 0,
  Artifacts: 1,
  Advanced: 2,
};

const EditOutboundMapping = ({ onBack, applyAll, isEditing }) => {
  const dispatch = useDispatch();

  const [previewResult, setPreviewResult] = useState("");
  const [isLoop, setIsLoop] = useState(false);
  const [tabId, setTabId] = useState(tabIds.Presets);
  const [cmEditor, setCmEditor] = useState();
  const [index, setIndex] = useState(0);
  const [loopRow, setLoopRow] = useState();

  const selectedSchema = useSelector(selectSelectedSchema);
  const currentArtifactId = useSelector(selectCurrentArtifactDictionaryId);
  const element = useSelector(selectCurrentOutboundElement);
  const tableSchema = useSelector(selectOutboundSchemaTable);
  const allTables = useSelector(selectAllOutboundSchemaTables);
  const complex = useSelector(selectArtifactComplex);

  const loopRowsList = useSelector(selectLoopRowsList);
  const { ConnectionId, ArtifactType } = selectedSchema;
  const { Loop, LoopDescriptor, LoopId, LoopIdOptions, ...rest } = element;

  useEffect(() => {
    const loopDetected = Loop != undefined && Loop.length > 0;
    setIsLoop(loopDetected);
  }, [Loop]);

  useEffect(() => {
    if (element.isLoopRow) setTabId(tabIds.Advanced);
  }, [element]);

  useEffect(() => {
    setIndex(tableSchema.rows.findIndex((x) => x.id == element.id));
    setPreviewResult("");
    setLoopRow();
  }, [isEditing, element.id, tableSchema]);

  // Switch to an adjacent table. Returns true if the switch happened.
  const switchTable = (forward) => {
    if (!allTables || allTables.length === 0) return false;
    const currentTableIndex = allTables.findIndex(
      (t) => t.table === tableSchema?.table
    );
    if (currentTableIndex === -1) return false;

    const nextTableIndex = forward ? currentTableIndex + 1 : currentTableIndex - 1;
    if (nextTableIndex < 0 || nextTableIndex >= allTables.length) return false;

    const nextTable = allTables[nextTableIndex];
    if (!nextTable?.rows?.length) return false;

    dispatch(
      setOutboundSchemaTable({ outboundSchemaTable: nextTable })
    );
    const targetRow = forward ? nextTable.rows[0] : nextTable.rows[nextTable.rows.length - 1];
    const targetIndex = forward ? 0 : nextTable.rows.length - 1;
    dispatch(updateOutboundElement({ element: targetRow }));
  
    return true;
  };

  const incrementElement = (forward) => {
    let current = index;
    let target = 0;
    if (forward) target = Math.min(current + 1, tableSchema.rows.length - 1);
    else target = Math.max(current - 1, 0);

    // If we haven't moved, infer we're at a table boundary
    if (target === current) {
      switchTable(forward);
      return;
    }

    dispatch(updateOutboundElement({ element: tableSchema.rows[target] }));
  };

  const incrementSegment = (forward) => {
    let current = index;
    let next = 0;

    let prevLoop = element.Loop;
    let prevSeg = element.Segment;

    let curLoop = element.Loop;
    let curSeg = element.Segment;
    let curPos = element.Position;
    const MAX_CHECK = 50; //avoid inf loop
    let i = 0;
    let newSeg = false;

    while (i < MAX_CHECK) {
      if (forward) {
        next = Math.min(current + 1, tableSchema.rows.length - 1);
      } else next = Math.max(current - 1, 0);

      if (next === current) {
        //no change means it reached either end � try switching tables
        switchTable(forward);
        return;
      }

      curLoop = tableSchema.rows[next].Loop;
      curSeg = tableSchema.rows[next].Segment;
      //first check if seg or loop name has changed
      // next check if position has "looped" back based on direction
      if (
        prevSeg !== curSeg ||
        prevLoop !== curLoop ||
        (forward && curPos >= tableSchema.rows[next].Position) ||
        (!forward && curPos <= tableSchema.rows[next].Position)
      ) {
        if (newSeg) {
          //changing seg again occurs if no position 1 ex loop rows
          next = current; //roll back
          break;
        }
        newSeg = true;
        prevLoop = curLoop;
        prevSeg = curSeg;
      }

      curPos = tableSchema.rows[next].Position;
      if (newSeg && curPos === 1) break; //stop at begining of segment

      current = next;
      i++;
    }
    dispatch(updateOutboundElement({ element: tableSchema.rows[next] }));
  };

  const onChangeFormula = (value) => {
    dispatch(updateOutboundElementField({ key: "UserMap", value: value }));
  };

  // Extract every field from complex:
  // selectArtifactComplex[i] -> ObjectName + Fields[j] -> Name
  // ObjectName.FieldName
  const autocompletions = useMemo(() => {
    const completionList = [];
    complex.forEach((object) => {
      completionList.push(
        ...object.Fields.map((field) => `${object.ObjectName}.${field.Name}`)
      );
    });
    return completionList;
  }, [complex]);

  const staged = useSelector(selectStagedOutboundEdits);
  const preview = () => {
    //if staged changes exist, use them in preview, element will already reflect staged changes due to reducer logic
    axios
      .post(
        "/mapschema/outbound/preview-script-by-artifact-type",
        {
          ArtifactType: selectedSchema.ArtifactType,
          ConnectionId: selectedSchema.ConnectionId,
          ArtifactDictionaryId: currentArtifactId,
          Script: element.UserMap ?? "",
        },
        null
      )
      .then((response) => {
        setPreviewResult(response.data);
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message: error.response?.data ?? error.message,
            buttonLabel: "Close",
          })
        );
        console.error("Error fetching data: ", error);
      });
  };

  const handleLoopCursorChange = (_, value) => {
    dispatch(
      updateOutboundElementField({
        key: "LoopId",
        value: value,
      })
    );
    if (element.isLoopRow) {
      dispatch(
        updateOutboundElementField({
          key: "DataValue",
          value: LoopDescriptor + "\r" + value,
        })
      );
    } else {
      const matchedLoopRow = (() => {
        const item = loopRowsList.find(
          (i) => i.LoopObjectId === element.LoopObjectId
        );
        return item
          ? {
            ...item,
            LoopId: value,
            DataValue: LoopDescriptor + "\r" + value,
          }
          : null;
      })();

      setLoopRow(matchedLoopRow);
    }
  };

  const handleClear = () => {
    dispatch(removeItemFromEditBatch({ key: element.id }));
    const baseRow = tableSchema?.rows?.find((r) => r.id === element.id);
    if (baseRow) {
      dispatch(updateOutboundElement({ element: baseRow }));
    }
  };

  const handleClearAll = () => {
    dispatch(clearItemsFromEditBatch());
    const baseRow = tableSchema?.rows?.find((r) => r.id === element.id);
    if (baseRow) {
      dispatch(updateOutboundElement({ element: baseRow }));
    }
  };

  return (
    <>
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton variant="secondary" onClick={onBack} sx={{ mr: 1 }}>
        Back
      </FooterButton>
      <FooterButton variant="secondary" onClick={handleClear} sx={{ mr: 1 }}>
        Clear
      </FooterButton>
      <FooterButton variant="secondary" onClick={handleClearAll} sx={{ mr: 1 }}>
        Clear All
      </FooterButton>
      <FooterButton variant="primary" onClick={applyAll}>
        Save
      </FooterButton>

      <Stack
        direction="row"
        sx={{
          width: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          gap: 1,
          p: 0,
          pt: "5px",
        }}
      >
        {/* Left column */}
        <Stack sx={{ width: "75%", minHeight: 0, flex: "1 1 75%", overflowY: "auto", overflowX: "hidden", pr: 1 }}>
          {/* Navigation row */}
          <Stack
            flexDirection={"row"}
            alignItems="center"
            sx={{ gap: 2 }}
          >
            <Stack
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              sx={{ flex: 1, minWidth: 0, gap: "20px" }}
            >
              <IconButton
                onClick={() => { incrementSegment(false); }}
                style={{ padding: 0, backgroundColor: "transparent" }}
              >
                <ArrowLeftIcon style={{ color: "red", width: "30px", height: "30px" }} />
              </IconButton>
              <IconButton
                onClick={() => { incrementElement(false); }}
                style={{ padding: 0, backgroundColor: "transparent" }}
              >
                <ArrowLeftIcon style={{ color: "red", width: "30px", height: "30px" }} />
              </IconButton>
              <InputLabel sx={{ mx: 1 }}>
                {element.Segment.split(" ")[0] +
                  (element.isLoopRow
                    ? ""
                    : element.Position.toString().padStart(2, "0"))}
              </InputLabel>
              <IconButton
                onClick={() => { incrementElement(true); }}
                style={{ padding: 0, backgroundColor: "transparent" }}
              >
                <ArrowRightIcon style={{ color: "red", width: "30px", height: "30px" }} />
              </IconButton>
              <IconButton
                onClick={() => { incrementSegment(true); }}
                style={{ padding: 0, backgroundColor: "transparent" }}
              >
                <ArrowRightIcon style={{ color: "red", width: "30px", height: "30px" }} />
              </IconButton>
            </Stack>

            <Stack
              flexDirection={"row"}
              alignItems={"center"}
              sx={{ flex: 1, minWidth: 0, gap: 1 }}
            >
              <InputLabel sx={{ whiteSpace: "nowrap" }}>Description:</InputLabel>
              <Input
                sx={{ flex: 1, minWidth: 0 }}
                value={
                  (element.Description ?? "") +
                  (element.DataValue
                    ? " - " + element.DataValue.trim().split(/\r?\n/)[0]
                    : "")
                }
              />
            </Stack>

            <Stack
              flexDirection={"row"}
              alignItems={"center"}
              sx={{ flex: 1, minWidth: 0, gap: 1 }}
            >
              <InputLabel sx={{ whiteSpace: "nowrap" }}>Table:</InputLabel>
              <Input
                sx={{ flex: 1, minWidth: 0 }}
                value={element.Table}
              />
            </Stack>
          </Stack>

          {/* Loop info row */}
          <Stack
            flexDirection="row"
            alignItems="center"
            sx={{ mt: 1, gap: 2, flexWrap: "nowrap" }}
          >
            <InputLabel sx={{ whiteSpace: "nowrap" }}>Loop:</InputLabel>
            <Input disabled style={{ width: 150 }} value={Loop} />
            <InputLabel sx={{ whiteSpace: "nowrap" }}>Loop Desc:</InputLabel>
            <Input disabled style={{ width: 200 }} value={LoopDescriptor} />
            <InputLabel sx={{ whiteSpace: "nowrap" }}>Loop Cursor:</InputLabel>
            <Autocomplete
              disabled={!isLoop}
              id="loop-id-autocomplete"
              disablePortal
              freeSolo
              options={LoopIdOptions ?? []}
              sx={{ flex: 1, minWidth: 200 }}
              inputValue={LoopId}
              onInputChange={handleLoopCursorChange}
              renderInput={(props) => (
                <TextField
                  variant="outlined"
                  {...props}
                  sx={{
                    "& .MuiInputBase-input": {
                      height: "0px",
                    },
                  }}
                />
              )}
            />
          </Stack>

          {/* Artifact Data and Advanced panels*/}
          <Stack
            direction="row"
            sx={{
              mt: "13px",
              gap: 1,
              height: 350,
              minHeight: 200,
              flexShrink: 0,
            }}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, flexShrink: 0 }}>
                Artifact Data
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <ArtifactDataPanel cmEditor={cmEditor} />
              </Box>
            </Stack>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, flexShrink: 0 }}>
                Advanced
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  minWidth: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <AdvancedPanel />
              </Box>
            </Stack>
          </Stack>

          {!element.isLoopRow && (
            <Box style={{ marginTop: 8, width: "100%" }}>
              <InputLabel>User Map {"(JavaScript code is expected)"}:</InputLabel>
              <ScriptEditor
                value={element.UserMap}
                onChange={onChangeFormula}
                variableCompletions={autocompletions}
                onCreate={setCmEditor}
                minHeight="120px"
              />
            </Box>
          )}
          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            style={{ marginTop: 8 }}
          >
            <Stack flexDirection={"row"} alignItems={"center"}>
              <Button
                className="btn-secondary"
                style={{ width: 160 }}
                onClick={() => { preview(); }}
                disabled={element.isLoopRow}
              >
                Preview
              </Button>
              <Input
                style={{ marginLeft: 10, width: 300 }}
                value={previewResult}
              />
            </Stack>
          </Stack>
        </Stack>

        {/* Right column */}
        <Stack
          sx={{
            width: "25%",
            minHeight: 0,
            flex: "1 1 25%",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          <PreviewArtifact
            connectionId={ConnectionId}
            artifactType={ArtifactType}
            templateElement={element}
            loopRow={loopRow}
          />
        </Stack>
      </Stack>
    </>
  );
};
export default EditOutboundMapping;
